/**
 * Portfolio Companies Hook
 *
 * Fetches portfolio companies (projects with dealLifecycle === 'portfolio_company')
 * with real-time Firestore updates.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  addDoc,
  doc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { Project, DealLifecycle, DealStructureType, DealLifecycleHistoryEntry } from './useProjects';
import { getEffectiveDomain, getOrganizationIdFromEmail } from './useProjects';

const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
]);

const INITIAL_SNAPSHOT_FALLBACK_MS = 6000;

const normalizeDomainList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return Array.from(new Set(
    value
      .filter((domain): domain is string => typeof domain === 'string')
      .map((domain) => domain.toLowerCase().trim().replace(/^@/, ''))
      .filter(Boolean)
  ));
};

const getVisibleDomainsFromOrganization = (organizationData: Record<string, unknown> | undefined): string[] => {
  if (!organizationData) return [];

  const directDomains = normalizeDomainList(organizationData.domains);
  if (directDomains.length > 0) return directDomains;

  const authConfig = organizationData.auth;
  if (authConfig && typeof authConfig === 'object') {
    const authDomains = normalizeDomainList((authConfig as Record<string, unknown>).allowedDomains);
    if (authDomains.length > 0) return authDomains;
  }

  return normalizeDomainList(organizationData.allowedDomains);
};

const getFallbackVisibleDomainsForEmail = (email: string | undefined | null): string[] => {
  if (!email) return [];

  const effectiveDomain = getEffectiveDomain(email);
  if (!effectiveDomain || PUBLIC_EMAIL_DOMAINS.has(effectiveDomain)) {
    return [];
  }

  if (effectiveDomain === 'corbelcap.com') {
    return ['corbelcap.com', 'corbelcap.ai'];
  }

  if (effectiveDomain === 'corbelcap.ai') {
    return ['corbelcap.ai', 'corbelcap.com'];
  }

  return [effectiveDomain];
};

/**
 * Input shape for creating a portfolio company directly.
 * All numeric values are entered in $M (millions) to match CreateProjectDialog convention.
 */
export interface CreatePortfolioCompanyInput {
  companyName: string;
  industry: string;
  dealStructureType?: DealStructureType;
  investmentDate?: Date;
  investmentAmount?: number;   // $M
  currentValuation?: number;   // $M
  revenue?: number;            // $M
  ebitda?: number;             // $M
  grossProfit?: number;        // $M
  netDebt?: number;            // $M
  dealSource?: string;
  notes?: string;
  dealContext?: string;
}

export interface PortfolioCompany extends Project {
  // Computed/enriched fields
  grossProfit?: number;
  grossMargin?: number;
  ebitdaMargin?: number;
  adjustedEbitda?: number;
  adjustedEbitdaMargin?: number;
  revenueGrowth?: number;
  grossProfitGrowth?: number;
  ebitdaGrowth?: number;
  // Credit metrics
  netDebt?: number;
  debtToEbitda?: number;
  // Budget comparison
  budgetRevenue?: number;
  budgetEbitda?: number;
  revenueVariancePct?: number;
  ebitdaVariancePct?: number;
  // Portfolio-specific fields
  investmentDate?: Date;
  investmentAmount?: number;
  currentValuation?: number;
  moic?: number; // Multiple on Invested Capital
  irr?: number; // Internal Rate of Return
}

export interface PortfolioMetrics {
  totalRevenue: number;
  totalEbitda: number;
  avgEbitdaMargin: number;
  avgRevenueGrowth: number;
  companyCount: number;
  sectorBreakdown: Record<string, number>;
  vintageBreakdown: Record<string, number>;
}

export function usePortfolioCompanies() {
  const [companies, setCompanies] = useState<PortfolioCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, userProfile } = useAuth();

  useEffect(() => {
    if (!user) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Safely convert Firestore timestamps, plain dates, strings, or numbers to Date
    const safeToDate = (val: any): Date => {
      if (!val) return new Date();
      if (typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      if (typeof val === 'number') return new Date(val);
      if (typeof val === 'string') return new Date(val);
      if (val.seconds != null) return new Date(val.seconds * 1000);
      if (val._seconds != null) return new Date(val._seconds * 1000); // JSON.stringify'd Timestamp
      return new Date();
    };

    const mapDocToPortfolioCompany = (doc: any): PortfolioCompany => {
      const data = doc.data();
      const FINANCIAL_SCALE = 1000;
      const revenue = (data.revenue || 0) * FINANCIAL_SCALE;
      const ebitda = (data.ebitda || 0) * FINANCIAL_SCALE;
      const grossProfit = (data.grossProfit || 0) * FINANCIAL_SCALE;
      const adjustedEbitda = (data.adjustedEbitda || 0) * FINANCIAL_SCALE;
      const netDebt = (data.netDebt || 0) * FINANCIAL_SCALE;

      return {
        id: doc.id,
        name: data.name || '',
        companyName: data.companyName || data.name || '',
        industry: data.industry || 'Other',
        stage: data.stage || 'closed',
        team: data.team || [],
        enterpriseValue: data.enterpriseValue,
        revenue,
        ebitda,
        grossProfit,
        adjustedEbitda,
        netDebt,
        createdBy: data.createdBy || '',
        createdAt: safeToDate(data.createdAt),
        updatedAt: safeToDate(data.updatedAt),
        dealLifecycle: data.dealLifecycle as DealLifecycle,
        dealStructureType: data.dealStructureType,
        dealLifecycleHistory: data.dealLifecycleHistory,
        metadata: data.metadata,
        visibleToDomains: data.visibleToDomains,
        grossMargin: revenue > 0 ? (grossProfit / revenue) * 100 : 0,
        ebitdaMargin: revenue > 0 ? (ebitda / revenue) * 100 : 0,
        adjustedEbitdaMargin: revenue > 0 ? (adjustedEbitda / revenue) * 100 : 0,
        revenueGrowth: data.revenueGrowth || 0,
        grossProfitGrowth: data.grossProfitGrowth || 0,
        ebitdaGrowth: data.ebitdaGrowth || 0,
        debtToEbitda: ebitda > 0 && netDebt ? netDebt / ebitda : undefined,
        budgetRevenue: data.budgetRevenue ? data.budgetRevenue * FINANCIAL_SCALE : undefined,
        budgetEbitda: data.budgetEbitda ? data.budgetEbitda * FINANCIAL_SCALE : undefined,
        revenueVariancePct: data.budgetRevenue && data.revenue
          ? ((data.revenue - data.budgetRevenue) / data.budgetRevenue) * 100
          : undefined,
        ebitdaVariancePct: data.budgetEbitda && data.ebitda
          ? ((data.ebitda - data.budgetEbitda) / data.budgetEbitda) * 100
          : undefined,
        investmentDate: data.investmentDate ? safeToDate(data.investmentDate) : undefined,
        investmentAmount: data.investmentAmount ? data.investmentAmount * FINANCIAL_SCALE : undefined,
        currentValuation: (data.currentValuation || data.enterpriseValue)
          ? (data.currentValuation || data.enterpriseValue) * FINANCIAL_SCALE
          : undefined,
        moic: data.metadata?.exitMultiple || (data.currentValuation && data.investmentAmount
          ? data.currentValuation / data.investmentAmount
          : undefined),
        irr: data.metadata?.exitIRR,
      };
    };

    // 1. Query for portfolio companies where user is in the team
    const teamQuery = query(
      collection(db, 'projects'),
      where('team', 'array-contains', user.uid),
      where('dealLifecycle', 'in', ['portfolio_company', 'exited'])
    );

    // 2. Query for domain-shared portfolio companies
    // Uses getEffectiveDomain() to support guest email overrides (e.g., personal Gmail → corbelcap.com).
    // Domain bridging is handled at the data layer: all projects store both
    // bridged domains in visibleToDomains, so querying for the user's own domain works.
    let domainQuery: any = null;
    if (user.email) {
      const domain = getEffectiveDomain(user.email);
      if (domain) {
        domainQuery = query(
          collection(db, 'projects'),
          where('visibleToDomains', 'array-contains', domain),
          where('dealLifecycle', 'in', ['portfolio_company', 'exited'])
        );
      }
    }

    let teamData: PortfolioCompany[] = [];
    let domainData: PortfolioCompany[] = [];
    let domainUnsub: () => void;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    let initialSnapshotResolved = false;

    const updateMerged = () => {
      initialSnapshotResolved = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = undefined;
      }

      const map = new Map<string, PortfolioCompany>();
      teamData.forEach(p => map.set(p.id, p));
      domainData.forEach(p => map.set(p.id, p));
      const merged = Array.from(map.values());
      merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setCompanies(merged);
      setLoading(false);
    };

    const runInitialFetchFallback = async () => {
      if (initialSnapshotResolved) return;

      console.warn('[usePortfolioCompanies] Initial onSnapshot did not resolve; falling back to getDocs');
      try {
        const [teamSnapshot, domainSnapshot] = await Promise.all([
          getDocs(teamQuery),
          domainQuery ? getDocs(domainQuery) : Promise.resolve(null),
        ]);

        if (initialSnapshotResolved) return;

        teamData = teamSnapshot.docs.map(mapDocToPortfolioCompany);
        domainData = domainSnapshot?.docs.map(mapDocToPortfolioCompany) || [];
        updateMerged();
      } catch (err: any) {
        if (initialSnapshotResolved) return;
        console.error('[usePortfolioCompanies] Initial fetch fallback failed:', err);
        setError(err?.message || 'Failed to load portfolio companies');
        setLoading(false);
      }
    };

    fallbackTimer = setTimeout(runInitialFetchFallback, INITIAL_SNAPSHOT_FALLBACK_MS);

    const teamUnsub = onSnapshot(
      teamQuery,
      (snapshot) => {
        teamData = snapshot.docs.map(mapDocToPortfolioCompany);
        updateMerged();
      },
      (err) => {
        console.error('Error fetching team portfolio companies:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    if (domainQuery) {
      domainUnsub = onSnapshot(
        domainQuery,
        (snapshot: any) => {
          domainData = snapshot.docs.map(mapDocToPortfolioCompany);
          updateMerged();
        },
        (err: any) => {
          console.error('Error fetching domain portfolio companies:', err);
        }
      );
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (teamUnsub) teamUnsub();
      if (domainUnsub) domainUnsub();
    };
  }, [user?.uid, user?.email]);

  // Calculate aggregate metrics
  const metrics = useMemo((): PortfolioMetrics => {
    const activeCompanies = companies.filter(c => c.dealLifecycle === 'portfolio_company');

    const totalRevenue = activeCompanies.reduce((sum, c) => sum + (c.revenue || 0), 0);
    const totalEbitda = activeCompanies.reduce((sum, c) => sum + (c.ebitda || 0), 0);
    const avgEbitdaMargin = activeCompanies.length > 0
      ? activeCompanies.reduce((sum, c) => sum + (c.ebitdaMargin || 0), 0) / activeCompanies.length
      : 0;
    const avgRevenueGrowth = activeCompanies.length > 0
      ? activeCompanies.reduce((sum, c) => sum + (c.revenueGrowth || 0), 0) / activeCompanies.length
      : 0;

    // Sector breakdown
    const sectorBreakdown: Record<string, number> = {};
    activeCompanies.forEach(c => {
      const sector = c.industry || 'Other';
      sectorBreakdown[sector] = (sectorBreakdown[sector] || 0) + 1;
    });

    // Vintage breakdown (by year of investment/creation)
    const vintageBreakdown: Record<string, number> = {};
    activeCompanies.forEach(c => {
      const year = (c.investmentDate || c.createdAt).getFullYear().toString();
      vintageBreakdown[year] = (vintageBreakdown[year] || 0) + 1;
    });

    return {
      totalRevenue,
      totalEbitda,
      avgEbitdaMargin,
      avgRevenueGrowth,
      companyCount: activeCompanies.length,
      sectorBreakdown,
      vintageBreakdown,
    };
  }, [companies]);

  // Get unique sectors
  const sectors = useMemo(() => {
    return ['all', ...Object.keys(metrics.sectorBreakdown)];
  }, [metrics]);

  // Get active portfolio companies only
  const activeCompanies = useMemo(() => {
    return companies.filter(c => c.dealLifecycle === 'portfolio_company');
  }, [companies]);

  // Get exited companies
  const exitedCompanies = useMemo(() => {
    return companies.filter(c => c.dealLifecycle === 'exited');
  }, [companies]);

  /**
   * Create a new portfolio company directly.
   *
   * Writes a document into the shared `projects` collection with
   * dealLifecycle = 'portfolio_company' from the start (skipping the
   * prospect → active_deal → portfolio_company progression).
   *
   * Use this when the user is adding an already-owned portfolio company
   * (e.g., acquired company imported from a prior fund) rather than
   * graduating a live deal through the pipeline.
   */
  const createPortfolioCompany = async (
    input: CreatePortfolioCompanyInput
  ): Promise<string> => {
    if (!user) throw new Error('User must be authenticated');

    const organizationId = userProfile?.organizationId ||
      (user.email ? getOrganizationIdFromEmail(user.email) : 'default-org');
    let visibleToDomains = getFallbackVisibleDomainsForEmail(user.email);

    if (organizationId && organizationId !== 'default-org') {
      const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
      if (orgDoc.exists()) {
        const orgVisibleDomains = getVisibleDomainsFromOrganization(
          orgDoc.data() as Record<string, unknown>
        );
        if (orgVisibleDomains.length > 0) {
          visibleToDomains = orgVisibleDomains;
        }
      }
    }

    // Seed deal lifecycle history so analytics match projects that reach
    // portfolio status through the normal toggle path.
    const historyEntry: DealLifecycleHistoryEntry = {
      from: null,
      to: 'portfolio_company',
      changedBy: user.uid,
      changedAt: new Date(),
      notes: 'Created directly as portfolio company',
    };

    // Required fields per isValidProject() in firestore.rules:62
    // (name, industry, stage, team, createdAt, createdBy)
    const newCompany: Record<string, unknown> = {
      name: input.companyName,
      companyName: input.companyName,
      industry: input.industry,
      stage: 'closed',
      dealLifecycle: 'portfolio_company' as DealLifecycle,
      dealLifecycleHistory: [historyEntry],
      team: [user.uid],
      visibleToDomains,
      organizationId,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Optional scalar fields — filter out undefined/NaN before write
    // (Firestore rejects undefined values; see CLAUDE.md pattern #3)
    const maybeAdd = (key: string, value: number | undefined) => {
      if (value !== undefined && !Number.isNaN(value)) {
        newCompany[key] = value;
      }
    };
    maybeAdd('revenue', input.revenue);
    maybeAdd('ebitda', input.ebitda);
    maybeAdd('grossProfit', input.grossProfit);
    maybeAdd('netDebt', input.netDebt);
    maybeAdd('investmentAmount', input.investmentAmount);
    maybeAdd('currentValuation', input.currentValuation);

    if (input.dealStructureType) {
      newCompany.dealStructureType = input.dealStructureType;
    }
    if (input.investmentDate) {
      newCompany.investmentDate = input.investmentDate;
    }
    if (input.dealContext && input.dealContext.trim()) {
      newCompany.dealContext = input.dealContext.trim();
    }

    // metadata: include only defined sub-fields so Firestore doesn't choke
    // on nested undefined values.
    const metadata: Record<string, unknown> = {};
    if (input.dealSource && input.dealSource.trim()) {
      metadata.dealSource = input.dealSource.trim();
    }
    if (input.notes && input.notes.trim()) {
      metadata.notes = input.notes.trim();
    }
    if (Object.keys(metadata).length > 0) {
      newCompany.metadata = metadata;
    }

    try {
      const docRef = await addDoc(collection(db, 'projects'), newCompany);
      return docRef.id;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create portfolio company';
      console.error('Error creating portfolio company:', err);
      throw new Error(message);
    }
  };

  return {
    companies,
    activeCompanies,
    exitedCompanies,
    metrics,
    sectors,
    loading,
    error,
    createPortfolioCompany,
  };
}
